import { Trans } from "@lingui/macro";
import { Box, Button, Typography } from "@mui/material";
import MapList from "./MapList";
import useUpdateMaps from "./useUpdateMaps";
import { useMemo } from "react";
import useMaps, { TilePackState } from "./useMaps";
import { ErrorBoundary, hasMinProfile, useUserContext } from "@WahooFitness/wahoo-offline-mfe";
import { Profile } from "@WahooFitness/cloud-client-types";

interface UpdateMapsEmbedProps {
  appToken: string;
}

const UpdateMapsEmbed = ({ appToken }: UpdateMapsEmbedProps) => {
  const { onUpdateAllClick, isLoading, sortedTilePacks } = useUpdateMaps(appToken);

  const hasUpdates = useMemo(
    () =>
      sortedTilePacks.some(
        (tilePack) => tilePack.state === TilePackState.SAVED && tilePack.upgradeSize > 0
      ),
    [sortedTilePacks]
  );

  const { currentUser } = useUserContext();

  const userIsAlphaPlus = useMemo(
    () => hasMinProfile(Profile.alpha, currentUser?.app?.profile),
    [currentUser]
  );

  const {
    mapPack,
    mapPackIsLoading,
    mapsSyncError,
    retrySync,
    sendDownloadMapTilePack,
    sendUpgradeMapTilePack,
  } = useMaps(appToken);

  return (
    <ErrorBoundary
      error={mapsSyncError}
      isLoading={isLoading || mapPackIsLoading}
      onReload={retrySync}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        {userIsAlphaPlus && (
          <Button
            size="large"
            variant="contained"
            onClick={onUpdateAllClick}
            disabled={!hasUpdates}
          >
            <Trans>Update maps</Trans>
          </Button>
        )}
        <Typography variant="prose-base" textAlign="center">
          <Trans>
            Your ELEMNT will download new maps in the background. Keep your ELEMNT turned on,
            plugged in to a power source, and connected to a Wi-Fi network until the updates are
            complete.
          </Trans>
        </Typography>
      </Box>
      {sortedTilePacks.length > 0 ? (
        <MapList
          appToken={appToken}
          editing={false}
          onUpdateTilePack={sendUpgradeMapTilePack}
          onAddTilePack={sendDownloadMapTilePack}
          tilePacks={sortedTilePacks}
          wifiNetworkCount={mapPack?.wifiNwCount || 0}
        />
      ) : (
        <Typography textAlign="center">
          <Trans>All maps are up to date.</Trans>
        </Typography>
      )}
    </ErrorBoundary>
  );
};

export default UpdateMapsEmbed;
